import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import type {
  ThematicEntityAttribute,
  ThematicEntity,
  EAVValueType,
  NewThematicEntityAttribute,
} from "@/types/thematicMaps";
import { EAV_VALUE_TYPES } from "@/types/thematicMaps";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;

interface EAVTableProps {
  attributes: ThematicEntityAttribute[];
  entities: ThematicEntity[];
  loading: boolean;
  onCreateAttribute: (
    data: Omit<NewThematicEntityAttribute, "user_id" | "map_id">,
  ) => Promise<ThematicEntityAttribute | null>;
  onDeleteAttribute: (id: string) => Promise<boolean>;
}

const VALUE_TYPE_LABELS: Record<EAVValueType, string> = {
  text: "Text",
  number: "Number",
  boolean: "Boolean",
  enum: "Enum",
};

const CONFIDENCE_COLOR = (c: number, source: string) => {
  if (source === "manual")
    return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
  if (c >= 0.9)
    return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
  if (c >= 0.7)
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
  return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
};

const SOURCE_LABELS: Record<string, string> = {
  ai: "AI",
  manual: "Reczne",
  inferred: "Wniosk.",
};

export function EAVTable({
  attributes,
  entities,
  loading,
  onCreateAttribute,
  onDeleteAttribute,
}: EAVTableProps) {
  const [newEntityId, setNewEntityId] = useState("");
  const [newAttrName, setNewAttrName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newType, setNewType] = useState<EAVValueType>("text");
  const [adding, setAdding] = useState(false);
  const [filterEntityId, setFilterEntityId] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const entityMap = new Map(entities.map((e) => [e.id, e]));

  const filteredAttributes =
    filterEntityId === "all"
      ? attributes
      : attributes.filter((a) => a.entity_id === filterEntityId);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAttributes.length / pageSize),
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedAttributes = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredAttributes.slice(start, start + pageSize);
  }, [filteredAttributes, safeCurrentPage, pageSize]);

  const handleFilterChange = (value: string) => {
    setFilterEntityId(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleAdd = async () => {
    if (!newEntityId || !newAttrName.trim() || !newValue.trim()) return;
    setAdding(true);
    await onCreateAttribute({
      entity_id: newEntityId,
      attribute_name: newAttrName.trim().toLowerCase(),
      value: newValue.trim(),
      value_type: newType,
      source: "manual",
      confidence: 1.0,
      notes: "",
      sort_order: 0,
    });
    setNewAttrName("");
    setNewValue("");
    setNewType("text");
    setAdding(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm("Usunac atrybut?")) {
      await onDeleteAttribute(id);
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Ladowanie atrybutow...
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Inline add form */}
      <div className="flex items-end gap-2">
        <Select value={newEntityId} onValueChange={setNewEntityId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Encja..." />
          </SelectTrigger>
          <SelectContent>
            {entities.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.canonical_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Atrybut..."
          value={newAttrName}
          onChange={(e) => setNewAttrName(e.target.value)}
          className="w-[150px]"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Input
          placeholder="Wartosc..."
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          className="w-[150px]"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Select
          value={newType}
          onValueChange={(v) => setNewType(v as EAVValueType)}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EAV_VALUE_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {VALUE_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={
            adding || !newEntityId || !newAttrName.trim() || !newValue.trim()
          }
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Dodaj
        </Button>
      </div>

      {/* Filter + page size + count */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterEntityId} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtruj encje..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie encje</SelectItem>
            {entities.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.canonical_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size} na strone
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filteredAttributes.length} atrybutow
        </span>
      </div>

      {/* Table */}
      {filteredAttributes.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Brak atrybutow. Uzyj "Wyciagnij EAV z AI" lub dodaj recznie.
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Encja</TableHead>
                <TableHead>Atrybut</TableHead>
                <TableHead>Wartosc</TableHead>
                <TableHead className="w-[80px]">Typ</TableHead>
                <TableHead className="w-[70px]">Pewnosc</TableHead>
                <TableHead className="w-[80px]">Zrodlo</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedAttributes.map((attr) => {
                const entity = entityMap.get(attr.entity_id);
                return (
                  <TableRow key={attr.id}>
                    <TableCell className="text-sm font-medium">
                      {entity?.canonical_name || attr.entity_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {attr.attribute_name}
                    </TableCell>
                    <TableCell className="text-sm">{attr.value}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {VALUE_TYPE_LABELS[attr.value_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`text-xs ${CONFIDENCE_COLOR(attr.confidence, attr.source)}`}
                      >
                        {attr.source === "manual"
                          ? "1.0"
                          : attr.confidence.toFixed(2)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {SOURCE_LABELS[attr.source] || attr.source}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(attr.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-muted-foreground">
                Strona {safeCurrentPage} z {totalPages} (
                {(safeCurrentPage - 1) * pageSize + 1}-
                {Math.min(
                  safeCurrentPage * pageSize,
                  filteredAttributes.length,
                )}{" "}
                z {filteredAttributes.length})
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safeCurrentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Poprz.
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={safeCurrentPage >= totalPages}
                >
                  Nast.
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
